import React, { useCallback, useLayoutEffect, useRef } from 'react';
import type { InputNode } from '../types';

type Props = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  error?: string;
};

type Variant = 'markdown-module' | 'report-module';

/**
 * 解析-展开模块的 HTML 内容渲染器（markdown-module / report-module 共用）。
 *
 * V1 结构（{type}/render.ts）用 contenteditable div + innerHTML 直接渲染 HTML：
 *   <div class="rte-richtext-container rte-{type}-container">
 *     <div class="rte-richtext-wrapper rte-{type}-wrapper">
 *       <div class="rte-richtext rte-{type}" contenteditable>${htmlContent}</div>
 *
 * 关键：模块内容是 HTML 表格字符串（来自 markdownAnalyze/reportAnalyze），
 * 必须当成真实 DOM 渲染成 <table>，不能像 textarea 那样转义成纯文本。
 * 因此这里走 contentEditable + 命令式 innerHTML，而非 antd Input.TextArea。
 *
 * 命令式写 innerHTML 的原因：受控 dangerouslySetInnerHTML 会在每次输入后
 * 重写 DOM、把光标弹回开头。所以只在「外部 value 与当前 DOM 不一致」时才同步，
 * 用户输入时（DOM 已是最新）跳过回写，光标不丢。
 */
function createModuleNode(variant: Variant, displayName: string) {
  const Comp: React.FC<Props> = React.memo(({ node, value, onChange, disabled, error }) => {
    const moduleIndex = node.moduleIndex ?? 0;
    const moduleKey = `${node.key}__module_${moduleIndex}`;
    const ref = useRef<HTMLDivElement>(null);

    const content = value ?? node.text ?? '';

    // 仅当外部值与 DOM 现状不同才回写，避免输入时光标跳到开头。
    useLayoutEffect(() => {
      const el = ref.current;
      if (el && el.innerHTML !== content) el.innerHTML = content;
    }, [content]);

    const handleInput = useCallback(
      (e: React.FormEvent<HTMLDivElement>) => onChange(moduleKey, e.currentTarget.innerHTML),
      [moduleKey, onChange],
    );

    const minHeight = typeof node.minHeight === 'number' ? node.minHeight : 80;
    const rawMaxHeight = typeof node.maxHeight === 'number' ? node.maxHeight : 240;
    const maxHeight = Math.max(rawMaxHeight, minHeight);

    return (
      <div
        className={`rte-richtext-container rte-${variant}-container ${error ? `rte-${variant}-error` : ''}`}
        data-key={node.key}
      >
        <div className={`rte-richtext-wrapper rte-${variant}-wrapper`} data-key={node.key}>
          <div
            ref={ref}
            className={`rte-richtext rte-${variant}`}
            data-type="richtext"
            data-node-type={variant}
            data-key={node.key}
            data-module-index={moduleIndex}
            data-placeholder={node.placeholder}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={handleInput}
            style={{ minHeight, maxHeight, overflow: 'auto' }}
          />
        </div>
      </div>
    );
  });
  Comp.displayName = displayName;
  return Comp;
}

export const ModuleHtmlNode = { createModuleNode };
